import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { dateTime } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { StatusBadge } from "@/components/StatusBadge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { FileText, Sparkle, Lock } from "@phosphor-icons/react";

export default function ContractTemplate() {
  const [active, setActive] = useState(null);
  const [versions, setVersions] = useState([]);
  const [mergeFields, setMergeFields] = useState([]);
  const [busy, setBusy] = useState("");

  const load = () => api.get("/contract-template").then((r) => {
    setActive(r.data.active); setVersions(r.data.versions); setMergeFields(r.data.merge_fields || []);
  });
  useEffect(() => { load(); }, []);

  const generate = async () => {
    setBusy("gen");
    try { const r = await api.post("/contract-template/generate"); toast.success(`Draft v${r.data.version} authored by Claude`); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(""); }
  };
  const lock = async (version) => {
    setBusy("lock");
    try { await api.post("/contract-template/lock", { version }); toast.success(`Template v${version} locked — ready for signing`); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(""); }
  };

  return (
    <div>
      <div className="mb-8 flex items-end justify-between">
        <div>
          <h1 className="font-heading text-3xl font-black tracking-tight">Contract Template</h1>
          <p className="mt-1 text-sm text-muted-foreground">Claude authors the legal copy with merge placeholders · review · lock. The app only fills &amp; e-signs.</p>
        </div>
        <Button data-testid="generate-template-btn" onClick={generate} disabled={busy === "gen"} className="gap-2">
          <Sparkle size={16} weight="fill" /> {busy === "gen" ? "Drafting…" : "Generate with Claude"}
        </Button>
      </div>

      <div className="mb-4 flex flex-wrap gap-1.5">
        {mergeFields.map((f) => (
          <span key={f} className="rounded-sm border border-border bg-card px-2 py-0.5 font-mono text-[11px] text-muted-foreground">{`{{${f}}}`}</span>
        ))}
      </div>

      {!active ? (
        <div className="rounded-sm border border-dashed border-border bg-card p-16 text-center">
          <FileText size={40} className="mx-auto mb-4 text-muted-foreground" />
          <h3 className="font-heading text-lg font-semibold">No template yet</h3>
          <p className="mx-auto mt-1 max-w-sm text-sm text-muted-foreground">Generate a draft with Claude, review the legal copy, then lock it before the first rep signs.</p>
        </div>
      ) : (
        <div className="rounded-sm border border-border bg-card">
          <div className="flex items-center justify-between border-b border-border px-5 py-3">
            <div className="flex items-center gap-3">
              <span className="font-heading font-semibold">Version {active.version}</span>
              <StatusBadge status={active.status === "locked" ? "active" : "draft"} />
              <span className="text-xs text-muted-foreground">by {active.generated_by} · {dateTime(active.created_at)}</span>
            </div>
            {active.status !== "locked" && (
              <Button data-testid="lock-template-btn" size="sm" variant="outline" className="gap-2" onClick={() => lock(active.version)} disabled={busy === "lock"}>
                <Lock size={15} /> Lock Template
              </Button>
            )}
          </div>
          <ScrollArea className="h-[520px] p-6">
            <pre data-testid="template-body" className="whitespace-pre-wrap font-sans text-sm leading-relaxed">{active.body}</pre>
          </ScrollArea>
        </div>
      )}

      {versions.length > 1 && (
        <div className="mt-4 text-xs text-muted-foreground">
          {versions.length} versions authored. Locking a version makes it authoritative for all new contract generations.
        </div>
      )}
    </div>
  );
}
